import { KeyboardArrowDown as KeyboardArrowDownIcon } from '@material-ui/icons';
import cx from 'classnames';
import { forwardRef, ForwardRefRenderFunction, PropsWithChildren, ReactNode } from 'react';

import { WithStyles, withStyles } from '@core/theme/utils/with-styles';

import { styles } from './ManageSection.styles';
import { TooltipIcon } from '../tooltip-icon';

export enum ManageSectionTheme {
  default = 'default', // Pages with white backgrounds
  legacy = 'legacy', // Pages with grey backgrounds
  grey = 'grey', // Overlays with a grey background for steps
}

export interface ManageSectionProps extends PropsWithChildren<WithStyles<typeof styles>> {
  action?: ReactNode;
  collapsed?: boolean;
  content?: ReactNode;
  description?: string;
  disableShadow?: boolean;
  divider?: boolean;
  id?: string;
  theme?: ManageSectionTheme;
  title?: string;
  titleSuffix?: ReactNode;
  tooltip?: string;
  onChangeCollapsed?: (collapsed: boolean) => void;
}

//we shouldn't use content prop, we can use children instead
//it would provide a more versetile component
//supporting wrapping elements instead of always passing content
//While the support will remain the same, by using a children prop instead of content
//TO-DO refactor this to only use `children` prop, instead of `content`.

const ManageSectionComponent: ForwardRefRenderFunction<HTMLDivElement, ManageSectionProps> = (
  {
    action,
    children,
    classes,
    collapsed,
    content,
    description,
    disableShadow = false,
    divider = true,
    id,
    theme = ManageSectionTheme.legacy,
    title,
    titleSuffix,
    tooltip,
    onChangeCollapsed,
  },
  ref
) => {
  const isCollapsible = typeof onChangeCollapsed !== 'undefined';

  return (
    <div className={cx(classes.root, classes[theme], { [classes.noShadow]: disableShadow })} ref={ref} id={id}>
      <div
        className={cx(classes.header, {
          [classes.divider]: divider && !collapsed,
          [classes.headerClickable]: isCollapsible,
          [classes.headerCollapsed]: isCollapsible && collapsed,
        })}
        onClick={isCollapsible ? () => onChangeCollapsed!(!collapsed) : undefined}
      >
        <div className={classes.headerContent}>
          {title && (
            <div className={classes.title}>
              {title}
              {titleSuffix}
              {tooltip && (
                <TooltipIcon
                  tooltip={tooltip}
                  classes={{
                    root: classes.info,
                    icon: classes.infoIcon,
                    tooltip: classes.infoTooltip,
                  }}
                />
              )}
            </div>
          )}
          {description && <span className={classes.description}>{description}</span>}
        </div>
        <div className={classes.headerActions}>
          {action}
          {isCollapsible && (
            <KeyboardArrowDownIcon
              className={cx(classes.accordionIcon, {
                [classes.accordionIconExpanded]: !collapsed,
              })}
            />
          )}
        </div>
      </div>
      {!collapsed && (
        <div className={classes.content}>
          {content}
          {children}
        </div>
      )}
    </div>
  );
};

export const ManageSection = withStyles(styles)(forwardRef(ManageSectionComponent));
